'use client'

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import { useSession } from 'next-auth/react'
import { apiClient } from '@/lib/api-client'
import type {
  AppNotification,
  MarkReadResponse,
  NotificationListResponse,
  UnreadCountResponse,
} from '@/types/notification'

// The badge polls a cheap count endpoint; the list is only fetched when the
// dropdown opens or the count changes, so an idle tab costs one small query.
const UNREAD_POLL_MS = 30_000
const DROPDOWN_PAGE_SIZE = 10

interface NotificationContextState {
  notifications: AppNotification[]
  unreadCount: number
  loading: boolean
  error: string | null
  refresh: () => Promise<void>
  markAsRead: (id: string) => Promise<void>
  markAllAsRead: () => Promise<void>
}

const NotificationContext = createContext<NotificationContextState | undefined>(
  undefined
)

export function NotificationProvider({
  children,
}: {
  children: React.ReactNode
}) {
  const { status } = useSession()
  const isAuthenticated = status === 'authenticated'

  const [notifications, setNotifications] = useState<AppNotification[]>([])
  const [unreadCount, setUnreadCount] = useState(0)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Guards a refresh triggered by the poll from racing one triggered by a click.
  const inFlight = useRef(false)

  const refresh = useCallback(async () => {
    if (!isAuthenticated || inFlight.current) return
    inFlight.current = true
    setLoading(true)
    try {
      const res = await apiClient.get<NotificationListResponse>(
        `/api/notifications?page=1&page_size=${DROPDOWN_PAGE_SIZE}`
      )
      setNotifications(res.items)
      setUnreadCount(res.unread_count)
      setError(null)
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setLoading(false)
      inFlight.current = false
    }
  }, [isAuthenticated])

  const pollUnreadCount = useCallback(async () => {
    if (!isAuthenticated) return
    try {
      const res = await apiClient.get<UnreadCountResponse>(
        '/api/notifications/unread-count'
      )
      setUnreadCount((prev) => {
        // A change in the count means something new landed — pull the list too.
        if (res.unread_count !== prev) void refresh()
        return res.unread_count
      })
    } catch {
      // A failed poll is not worth surfacing; the next tick retries.
    }
  }, [isAuthenticated, refresh])

  useEffect(() => {
    if (!isAuthenticated) {
      setNotifications([])
      setUnreadCount(0)
      return
    }
    void refresh()
    const timer = setInterval(() => void pollUnreadCount(), UNREAD_POLL_MS)
    return () => clearInterval(timer)
  }, [isAuthenticated, refresh, pollUnreadCount])

  const markAsRead = useCallback(async (id: string) => {
    // Optimistic: the receipt is idempotent server-side, so a failure is harmless.
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, is_read: true } : n))
    )
    try {
      const res = await apiClient.post<MarkReadResponse>(
        `/api/notifications/${id}/read`
      )
      setUnreadCount(res.unread_count)
    } catch {
      setUnreadCount((prev) => prev)
    }
  }, [])

  const markAllAsRead = useCallback(async () => {
    setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })))
    try {
      const res = await apiClient.post<MarkReadResponse>(
        '/api/notifications/read-all'
      )
      setUnreadCount(res.unread_count)
    } catch (e) {
      setError((e as Error).message)
    }
  }, [])

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        unreadCount,
        loading,
        error,
        refresh,
        markAsRead,
        markAllAsRead,
      }}
    >
      {children}
    </NotificationContext.Provider>
  )
}

export function useNotifications(): NotificationContextState {
  const ctx = useContext(NotificationContext)
  if (!ctx) {
    throw new Error(
      'useNotifications must be used within a NotificationProvider'
    )
  }
  return ctx
}
